import { useMemo, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import DatePicker from '../../UI/DatePicker';
import { useGetInstructorRosterListQuery } from '@/services/lesson.service';
import {
  parseDateLocal,
  formatDateLocal,
  isValidDateString
} from '@/utils/common';
import InstructorRosterTable from './InstructorRosterTable';
import InstructorMonthlyCalendar from './InstructorMonthlyCalendar';
import { LessonSchedule } from '@/constants/Interfaces/CommonInterfaces';
import useInstructorRosterStore from '@/stores/instructorRosterStore';
import { ROUTE_PATHS } from '@/constants/paths';
import { useGetInstructorDetailsQuery } from '@/services';

interface InstructorInterface {
  name: string;
}

const InstructorRoster = () => {
  const { id, date, type } = useParams();
  const navigate = useNavigate();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const {
    selectedDate,
    selectedType,
    isLoading: storeLoading,
    error: storeError,
    setSelectedDate,
    setSelectedType,
    setSelectedInstructor,
    setLessonSchedule,
    setIsLoading,
    setError
  } = useInstructorRosterStore();

  const payload = useMemo(() => {
    const dateStr = selectedDate ?? '';
    return { id: id, queryStr: `date=${dateStr}&type=${selectedType}` };
  }, [id, selectedDate, selectedType]);

  // Month dates are computed inside InstructorRosterTable for rendering

  const { data, isLoading, isFetching, error, refetch } =
    useGetInstructorRosterListQuery(
      payload as { id?: string; queryStr: string }
    );

  const instructorDetails = useGetInstructorDetailsQuery(id ? Number(id) : 0)
    .data as InstructorInterface | undefined;
  // Sync API data with store
  useEffect(() => {
    if (data) {
      const { records: schedule = [] } = data as unknown as {
        records: LessonSchedule[];
      };
      setLessonSchedule(schedule as LessonSchedule[]);
    }
  }, [data, setLessonSchedule]);

  // Sync loading state with store
  useEffect(() => {
    setIsLoading(isLoading || isFetching);
  }, [isLoading, isFetching, setIsLoading]);

  // Sync error state with store
  useEffect(() => {
    setError(error ? 'Failed to load roster data' : null);
  }, [error, setError]);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Handle view type change (Day/Month)
  const handleViewTypeChange = useCallback(
    (v: 'Day' | 'Month') => {
      setSelectedType(v);
    },
    [setSelectedType]
  );

  // Handle lesson click (not used in Month table directly)

  // Initialize store from URL params
  useEffect(() => {
    if (id) {
      const numId = Number(id);
      if (!Number.isNaN(numId)) setSelectedInstructor(numId);
    }
    if (date && isValidDateString(date)) {
      setSelectedDate(date);
    }
    if (type === 'Day' || type === 'Month') {
      setSelectedType(type);
    }
  }, [id, date, type, setSelectedInstructor, setSelectedDate, setSelectedType]);

  // Keep URL path in sync with store state
  useEffect(() => {
    const instructorId = id || '';
    const currentDate = selectedDate || '';
    const currentType = selectedType || 'Day';
    // Build target path using ROUTE_PATHS.INSTRUCTOR_ROSTER_LIST
    const base =
      ROUTE_PATHS?.INSTRUCTOR_ROSTER_LIST || '/daily-roster/instructor';
    const target = `${base}/${instructorId}/${currentDate}/${currentType}`;
    const currentPath = window.location.pathname;
    if (currentPath !== target && instructorId) {
      navigate(target, { replace: true });
    }
  }, [id, selectedDate, selectedType, navigate]);

  // Handle horizontal scroll
  const handleScrollLeft = useCallback(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({
        left: -300,
        behavior: 'smooth'
      });
    }
  }, []);

  const handleScrollRight = useCallback(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollBy({
        left: 300,
        behavior: 'smooth'
      });
    }
  }, []);

  if (storeError) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-white'>
        <div className='text-center p-8'>
          <AlertTriangle className='w-16 h-16 text-red-500 mx-auto mb-4' />
          <h2 className='text-2xl font-bold text-gray-800 mb-2'>
            Error Loading Roster
          </h2>
          <p className='text-gray-600 mb-4'>
            Failed to load the daily roster data.
          </p>
          <button
            onClick={handleRefresh}
            className='px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors'
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='relative'>
        <div className='mb-4 px-4 pt-4 flex w-full justify-between items-center'>
          <h4 className='text-left'>
            Instructor:{' '}
            <span className='font-bold'>{instructorDetails?.name}</span>
          </h4>
        </div>

        <div className=' lg:pb-0 pb-0 bg-gradient-to-br from-slate-50 to-white'>
          <div className='mb-0 lg:mb-0 bg-white shadow-sm border border-gray-200 overflow-hidden'>
            {/* Controls Section */}
            <div className='mb-4 px-4 pt-4'>
              <div className='flex flex-col sm:flex-row sm:items-center gap-3'>
                <div className='flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-start'>
                  {/* Refresh Button */}
                  <button
                    onClick={handleRefresh}
                    disabled={storeLoading || isFetching}
                    className='flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors text-sm'
                    aria-label='Refresh instructor roster data'
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${storeLoading || isFetching ? 'animate-spin' : ''}`}
                    />
                    Refresh
                  </button>

                  {/* View Type Buttons */}
                  <div className='flex border border-gray-300 rounded-lg overflow-hidden'>
                    <button
                      onClick={() => handleViewTypeChange('Day')}
                      className={`px-4 py-2 font-semibold text-sm transition-colors ${
                        selectedType === 'Day'
                          ? 'bg-blue-500 text-white'
                          : 'bg-white text-slate-700 hover:bg-gray-50'
                      }`}
                      aria-label='Switch to daily view'
                    >
                      Daily
                    </button>
                    <button
                      onClick={() => handleViewTypeChange('Month')}
                      className={`px-4 py-2 font-semibold text-sm transition-colors ${
                        selectedType === 'Month'
                          ? 'bg-blue-500 text-white'
                          : 'bg-white text-slate-700 hover:bg-gray-50'
                      }`}
                      aria-label='Switch to monthly view'
                    >
                      Month
                    </button>
                  </div>
                </div>

                {/* Date Picker with Navigation Arrows - Only show for daily view */}
                {selectedType === 'Day' && (
                  <div className='flex items-center gap-2 w-full sm:w-auto sm:ml-auto'>
                    <div className='flex items-center border border-gray-300 rounded-lg bg-white shadow-sm flex-1 sm:flex-none'>
                      <DatePicker
                        value={
                          selectedDate ? parseDateLocal(selectedDate) : null
                        }
                        onChange={date => {
                          const next = date ? formatDateLocal(date) : null;
                          if (next !== selectedDate) {
                            setSelectedDate(next);
                          }
                        }}
                        className='w-full sm:w-40 border-none focus:ring-0 px-2 py-1.5 rounded-lg'
                        aria-label='Select date for roster'
                      />
                    </div>
                    <button
                      onClick={handleScrollLeft}
                      className='flex items-center justify-center w-10 h-10 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm'
                      aria-label='Scroll table left'
                      title='Scroll left'
                    >
                      <ChevronLeft className='w-5 h-5 text-slate-700' />
                    </button>
                    <button
                      onClick={handleScrollRight}
                      className='flex items-center justify-center w-10 h-10 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm'
                      aria-label='Scroll table right'
                      title='Scroll right'
                    >
                      <ChevronRight className='w-5 h-5 text-slate-700' />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Content Section */}
            <div
              ref={tableContainerRef}
              className='overflow-auto border border-gray-200 h-[calc(100vh-170px)]'
            >
              {selectedType === 'Day' ? (
                <InstructorRosterTable instructorDetails={instructorDetails} />
              ) : (
                <InstructorMonthlyCalendar />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InstructorRoster;
